// Hand the main thread back to the compositor for exactly one frame.
//
// ── Why boot needs this (WS-PROD-BOOT-ONE-1619MS-BLOCKING-TASK-1) ────────
// `bootShell` mounts the shell with smbls `create()`, and building that
// element tree is one long SYNCHRONOUS block (measured 1.6–1.9 s on a
// warm box). Whatever task the boot happens to be running in when it
// reaches the mount therefore absorbs the whole build:
//   • warm-start snapshot path — the mount is reached with no network await
//     in between, so it lands inside the ENTRY MODULE'S OWN evaluation task:
//     one 1846 ms task, nothing painted, FCP 2152 ms (dev.my.symbols.app,
//     3 loads, visible tab, 2026-08-22).
//   • cold path — the mount lands inside the SDK response continuation:
//     a 1726 ms `resolve-promise` task (my.symbols.app, same pass).
// index.html already carries a themed boot spiral that is in the DOM from
// the first byte; the browser simply never gets a frame to draw it.
//
// One bounded yield before each mount site restores that frame. It does NOT
// make the tree build cheaper — that cost is tracked separately — it stops
// the build from eating first paint too.
//
// ── Why it can never wedge a boot ────────────────────────────────────────
// `requestAnimationFrame` DOES NOT FIRE in a background tab. A bare
// `await rAF` before the mount would leave a backgrounded boot unmounted
// forever (and every prerender/bfcache-restore boot with it). So the frame
// wait always races a hard `maxWaitMs` bound and resolves at most once.
// The resolved value reports which arm won: `true` = a frame really
// happened, `false` = the bound fired (or there is no window to paint in).
//
// Frank note: fully self-contained — no bare sibling-export reads, so it
// survives being frank.toJSON'd on its own (same rule as themeMode.js).
export const yieldToPaint = (
  win = typeof window !== 'undefined' ? window : null,
  maxWaitMs = 50
) => {
  const timer =
    win && typeof win.setTimeout === 'function'
      ? (cb, ms) => win.setTimeout(cb, ms)
      : typeof setTimeout === 'function'
        ? (cb, ms) => setTimeout(cb, ms)
        : null
  // No window and no timer means no compositor to yield to — a node/SSR
  // render. Resolve now rather than costing the caller a tick for nothing.
  if (!win || !timer) return Promise.resolve(false)
  // A HIDDEN tab draws no frame and clamps its timers (the 50 ms bound fired
  // 0.76-1 s late on dev, three times per boot): nothing can paint, so there
  // is nothing to wait for. Visible tabs are unchanged.
  if (win.document && win.document.hidden === true) return Promise.resolve(false)
  const raf = typeof win.requestAnimationFrame === 'function' ? win.requestAnimationFrame : null
  return new Promise((resolve) => {
    let settled = false
    const finish = (painted) => {
      if (settled) return
      settled = true
      resolve(painted)
    }
    timer(() => finish(false), maxWaitMs > 0 ? maxWaitMs : 0)
    if (!raf) return
    try {
      // rAF runs BEFORE the frame is drawn; the 0 ms timer after it lands
      // AFTER, so resuming here means the paint has actually happened.
      raf.call(win, () => timer(() => finish(true), 0))
    } catch (_) {
      finish(false)
    }
  })
}
